import React, { useEffect, useMemo, useState } from "react";
import copy from "copy-to-clipboard";
import { toast } from "react-toastify";
import ApiService from "../../../../../service/ApiService";
import { apiPaths } from "../../../../../service/apiPaths";
import { IService } from "../../../../../components/ModernTable/types";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Modal from "../../../../../components/Model";
import { AlertCircle, BadgeCheck, CheckCircle } from "lucide-react";

const Schedule = ({ campaignId }: { campaignId?: string }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeId } = useParams();
  const [form, setForm] = useState({
    campaignId: campaignId || "",
    leadName: "",
    companyName: "",
    LinkedInData: "",
    email: "",
    services: "",
    leadStatus: "New",
    companyWebsite: "",
    linkedInLink: "",
    description: "",
    linkedInLeadStatus: "",
    instagramLink: "",
    leadWebLink: "",
    personalization: "",
    subjectData: "",
    follow1: "",
    follow2: "",
    follow3: "",
    follow4: "",
    aiData: "",
    query: "",
    EmailData: "",
    SubjectData: "",
    SubjectF1: "",
    SubjectF2: "",
    SubjectF3: "",
    SubjectF4: "",
    scheduled: false,
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allCampaigns, setAllCampaigns] = useState<any[]>([]);
  const [services, setServices] = useState<IService[]>([]);
  const [instantlyCampaigns, setInstantlyCampaigns] = useState<any[]>([]);
  const [instantlyCampaignId, setInstantlyCampaignId] = useState<string>("");
  const [campaignIdToName, setCampaignIdToName] = useState<Map<string, string>>(
    new Map()
  );
  const [campaignName, setCampaignName] = useState<string>("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isAiOpen, setIsAiOpen] = useState(false);
  const [isQueryOpen, setIsQueryOpen] = useState(false);
  const [emailDeliverability, setEmailDeliverability] = useState<
    "unknown" | "deliverable" | "undeliverable"
  >("unknown");
  const [extra, setExtra] = useState<any>({});
  const [hasShownScheduledAlert, setHasShownScheduledAlert] = useState(false);

  // Normalize various truthy/falsy representations to boolean
  const toBool = (value: any): boolean => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const s = value.trim().toLowerCase();
      if (["true", "1", "yes", "y"].includes(s)) return true;
      if (["false", "0", "no", "n", ""].includes(s)) return false;
    }
    return false;
  };

  // Draft storage key
  const draftKey = useMemo(() => {
    const keyId =
      (routeId as any) || (campaignId as any) || editId || "unknown";
    return `scheduleDraft:${keyId}`;
  }, [routeId, campaignId, editId]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next: any = { ...prev, [name]: value };
      // Keep LinkedInData (UI Personalization) and personalization in sync
      if (name === "LinkedInData") {
        next.personalization = value;
      }
      if (name === "personalization" && !prev.LinkedInData) {
        next.LinkedInData = value;
      }
      return next;
    });
  };

  const fetchCampaignData = async () => {
    setLoading(true);
    try {
      // If a specific campaign is selected, fetch its list via campaignGetList; otherwise fall back to overall list
      const endpoint =
        typeof campaignId === "string" && campaignId
          ? `${apiPaths.campaignGetList}?campaignId=${campaignId}&page=1&limit=10000`
          : `${apiPaths.campaignGetAll}?page=1&limit=10000`;

      const response = await ApiService({
        method: "GET",
        endpoint,
      });

      // Support multiple possible response shapes safely
      const sourceList = Array.isArray(response?.campaigns)
        ? response.campaigns
        : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response)
        ? response
        : [];

      const normalizedCampaigns = (sourceList || []).map((c: any) => ({
        ...c,
        services:
          typeof c?.services === "object"
            ? c?.services?._id || ""
            : c?.services || "",
        follow1: c?.follow1 ?? c?.Follow1 ?? "",
        follow2: c?.follow2 ?? c?.Follow2 ?? "",
        follow3: c?.follow3 ?? c?.Follow3 ?? "",
        follow4: c?.follow4 ?? c?.Follow4 ?? "",
      }));
      setAllCampaigns(normalizedCampaigns);
      // If the API also returns services list, prefer it
      if (Array.isArray(response?.service)) {
        setServices(response.service);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to fetch campaigns");
    } finally {
      setLoading(false);
    }
  };

  // Prefill the form from API when editing via route id
  useEffect(() => {
    // Log the current URL to debug page parameter
    console.log(
      "Schedule component mounted - URL:",
      window.location.href,
      "Page from URL:",
      new URLSearchParams(window.location.search).get("page")
    );
    const fetchOne = async () => {
      const effectiveId = routeId || campaignId;
      if (!effectiveId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await ApiService({
          method: "GET",
          endpoint: `${apiPaths.campaignEdit}${effectiveId}`,
        });
        const item: any = (res && (res.data || res.item || res)) || {};
        // Capture campaign name from nested object or top-level fallback
        setCampaignName(
          (item?.campaignId &&
            (item?.campaignId?.campaignName || item?.campaignId?.name)) ||
            item?.campaignName ||
            ""
        );
        const normalized = {
          campaignId:
            typeof item?.campaignId === "object"
              ? item?.campaignId?._id || ""
              : item?.campaignId || "",
          leadName: item?.leadName || "",
          companyName: item?.companyName || "",
          email: item?.email || "",
          services:
            typeof item?.services === "object"
              ? item?.services?._id || ""
              : item?.services || "",
          leadStatus: item?.leadStatus || "New",
          EmailData: item?.EmailData || "",
          LinkedInData: item?.LinkedInData || item?.personalization || "",
          SubjectData: item?.SubjectData || "",
          SubjectF1: item?.SubjectF1 || item?.subjectF1 || "",
          SubjectF2: item?.SubjectF2 || item?.subjectF2 || "",
          SubjectF3: item?.SubjectF3 || item?.subjectF3 || "",
          SubjectF4: item?.SubjectF4 || item?.subjectF4 || "",
          companyWebsite: item?.companyWebsite || "",
          linkedInLink: item?.linkedInLink || "",
          description: item?.description || "",
          linkedInLeadStatus: item?.linkedInLeadStatus || "",
          instagramLink: item?.instagramLink || "",
          leadWebLink: item?.leadWebLink || "",
          personalization: item?.personalization || "",
          subjectData:
            item?.subjectData || item?.emailSubject || item?.subject || "",
          follow1: (item?.follow1 ?? item?.Follow1) || "",
          follow2: (item?.follow2 ?? item?.Follow2) || "",
          follow3: (item?.follow3 ?? item?.Follow3) || "",
          follow4: (item?.follow4 ?? item?.Follow4) || "",
          aiData: item?.aiData || "",
          query: item?.query || "",
          scheduled: toBool(item?.scheduled),
        };
        // Merge fallback values from payload/custom_variables if present
        const p: any = item?.payload || {};
        const cv: any = p?.custom_variables || {};
        const merged = { ...normalized } as any;
        if (!merged.leadName && (p.firstName || p.lastName)) {
          merged.leadName = `${p.firstName || ""} ${p.lastName || ""}`.trim();
        }
        if (!merged.companyName && p.companyName)
          merged.companyName = p.companyName;
        if (!merged.email && p.email) merged.email = p.email;
        if (!merged.companyWebsite && (p.website || p.company_website)) {
          merged.companyWebsite = p.website || p.company_website;
        }
        if (
          !merged.personalization &&
          (p.personalization || cv.personalization)
        ) {
          merged.personalization =
            p.personalization || cv.personalization || "";
        }
        // Ensure UI textarea bound to LinkedInData shows personalization if present
        if (!merged.LinkedInData && (p.personalization || cv.personalization)) {
          merged.LinkedInData = p.personalization || cv.personalization || "";
        }
        // Subjects (support multiple locations and casings)
        const tl: any = item || {};
        // Primary subject
        if (
          !merged.SubjectData &&
          (p.email_subject_f || cv.email_subject_f || tl.email_subject_f)
        ) {
          merged.SubjectData =
            p.email_subject_f || cv.email_subject_f || tl.email_subject_f || "";
        }
        if (
          !merged.subjectData &&
          (p.email_subject_f || cv.email_subject_f || tl.email_subject_f)
        ) {
          merged.subjectData =
            merged.subjectData ||
            p.email_subject_f ||
            cv.email_subject_f ||
            tl.email_subject_f ||
            "";
        }
        // Follow-up subjects F1..F4
        if (
          !merged.SubjectF1 &&
          (cv.email_subject_f1 || p.email_subject_f1 || tl.email_subject_f1)
        ) {
          merged.SubjectF1 =
            cv.email_subject_f1 || p.email_subject_f1 || tl.email_subject_f1;
        }
        if (
          !merged.SubjectF2 &&
          (cv.email_subject_f2 || p.email_subject_f2 || tl.email_subject_f2)
        ) {
          merged.SubjectF2 =
            cv.email_subject_f2 || p.email_subject_f2 || tl.email_subject_f2;
        }
        if (
          !merged.SubjectF3 &&
          (cv.email_subject_f3 || p.email_subject_f3 || tl.email_subject_f3)
        ) {
          merged.SubjectF3 =
            cv.email_subject_f3 || p.email_subject_f3 || tl.email_subject_f3;
        }
        if (
          !merged.SubjectF4 &&
          (cv.email_subject_f4 || p.email_subject_f4 || tl.email_subject_f4)
        ) {
          merged.SubjectF4 =
            cv.email_subject_f4 || p.email_subject_f4 || tl.email_subject_f4;
        }
        // Follows
        if (!merged.follow1 && (cv.follow_1 || p.follow_1)) {
          merged.follow1 = cv.follow_1 || p.follow_1 || "";
        }
        if (!merged.follow2 && (cv.follow_2 || p.follow_2)) {
          merged.follow2 = cv.follow_2 || p.follow_2 || "";
        }
        if (!merged.follow3 && (cv.follow_3 || p.follow_3)) {
          merged.follow3 = cv.follow_3 || p.follow_3 || "";
        }
        if (!merged.follow4 && (cv.follow_4 || p.follow_4)) {
          merged.follow4 = cv.follow_4 || p.follow_4 || "";
        }
        setForm(merged as any);
        setEditId(item?._id || effectiveId);
        // Capture additional fields for the Full Response popup if available
        setExtra({
          date: item?.date || item?.createdAt || item?.updatedAt || "",
          app: item?.app,
          app_probability: item?.app_probability,
          indicators: item?.indicators,
          dev_opportunities: item?.dev_opportunities,
          ai_potential: item?.ai_potential,
          bde_recommendation: item?.bde_recommendation,
          source_urls: item?.source_urls,
          full_response: item?.full_response || item?.aiData || "",
          response_file: item?.response_file || item?.responseFile || "",
        });
      } catch (err: any) {
        setError(err?.message || "Failed to load details");
      } finally {
        setLoading(false);
      }
    };
    fetchOne();
    const fetchInstantly = async () => {
      try {
        const instantlyResp = await ApiService({
          method: "GET",
          endpoint: apiPaths.instantlyCampaigns,
        });
        const list: any[] = Array.isArray(instantlyResp)
          ? instantlyResp
          : Array.isArray(instantlyResp?.data)
          ? instantlyResp.data
          : Array.isArray(instantlyResp?.items)
          ? instantlyResp.items
          : Array.isArray(instantlyResp?.campaigns)
          ? instantlyResp.campaigns
          : [];
        const normalized = (list || []).map((c: any) => ({
          id: c?.id || c?._id || c?.campaignId || "",
          name: (c?.name || c?.campaignName || c?.title || "").trim(),
        }));
        setInstantlyCampaigns(normalized);
      } catch {}
    };
    fetchInstantly();
    const fetchServicesData = async () => {
      try {
        const result = await ApiService({
          method: "GET",
          endpoint: `${apiPaths.services}`,
        });
        if (Array.isArray(result)) setServices(result as any);
      } catch {}
    };
    fetchServicesData();
    // Optionally load campaigns/services list as well
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, campaignId]);

  // Load local campaign masters once to map id -> name
  useEffect(() => {
    const loadMasters = async () => {
      try {
        const mastersResp = await ApiService({
          method: "GET",
          endpoint: apiPaths.campaignDataGetAll,
        });
        const masters: any[] = Array.isArray(mastersResp)
          ? mastersResp
          : Array.isArray(mastersResp?.data)
          ? mastersResp.data
          : Array.isArray(mastersResp?.items)
          ? mastersResp.items
          : [];
        const idToName = new Map<string, string>();
        for (const m of masters) {
          const mid = m?._id || m?.id || m?.campaignId;
          const nm = (m?.campaignName || m?.name || "").trim();
          if (mid && nm) idToName.set(String(mid), nm);
        }
        setCampaignIdToName(idToName);
      } catch {}
    };
    loadMasters();
  }, []);

  // Auto-select Instantly campaign when we know the parent local campaign id
  useEffect(() => {
    const localId = form.campaignId || (campaignId as any) || "";
    if (!localId || instantlyCampaignId) return;
    const localName = (
      campaignName ||
      campaignIdToName.get(String(localId)) ||
      ""
    ).trim();
    if (!localName) return;
    const match = instantlyCampaigns.find(
      (c: any) => (c?.name || "").toLowerCase() === localName.toLowerCase()
    );
    if (match?.id) setInstantlyCampaignId(match.id);
  }, [
    form.campaignId,
    campaignId,
    campaignName,
    campaignIdToName,
    instantlyCampaigns,
    instantlyCampaignId,
  ]);
  const verifyEmail = async () => {
    try {
      const verifyResp = await fetch(
        `https://api.bouncify.io/v1/verify?apikey=rwb1m57irlxnf0xsbjotb0i5e19k3wwh&email=${encodeURIComponent(
          form.email.trim()
        )}`
      );
      const verifyJson: any = await verifyResp.json().catch(() => ({}));
      const isDeliverable =
        (verifyJson?.result || "").toString().toLowerCase() === "deliverable";
      setEmailDeliverability(isDeliverable ? "deliverable" : "undeliverable");
      if (!isDeliverable) {
        toast.error(
          verifyJson?.reason
            ? `Email not deliverable: ${verifyJson.reason}`
            : "Email is not deliverable per verification"
        );
        // Update status to "Email Issue" so table reflects this state
        try {
          const idToUpdate = editId || (routeId as any) || (campaignId as any);
          if (idToUpdate) {
            await ApiService({
              method: "PUT",
              endpoint: `${apiPaths.campaignEdit}${idToUpdate}`,
              data: { leadStatus: "Email Issue", scheduled: false },
            });
            setForm((prev: any) => ({ ...prev, leadStatus: "Email Issue" }));
          }
        } catch {}
        return;
      }
    } catch (e: any) {
      toast.error(e?.message || "Email verification failed");
      return;
    }
  };
  useEffect(() => {
    if (form.email) {
      verifyEmail();
    }
  }, [form.email]);
  // Load draft from localStorage and merge into editable fields
  useEffect(() => {
    try {
      const raw =
        typeof window !== "undefined" ? localStorage.getItem(draftKey) : null;
      if (raw) {
        const draft = JSON.parse(raw || "{}");
        setForm((prev) => ({
          ...prev,
          personalization: draft?.personalization ?? prev.personalization,
          subjectData: draft?.subjectData ?? prev.subjectData,
          query: draft?.query ?? prev.query,
          follow1: draft?.follow1 ?? prev.follow1,
          follow2: draft?.follow2 ?? prev.follow2,
          follow3: draft?.follow3 ?? prev.follow3,
          follow4: draft?.follow4 ?? prev.follow4,
        }));
        if (draft?.instantlyCampaignId)
          setInstantlyCampaignId(draft.instantlyCampaignId);
        if (draft?.emailDeliverability) {
          setEmailDeliverability(draft.emailDeliverability);
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  // Persist email deliverability so color survives reloads
  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      if (emailDeliverability === "unknown") return;
      const raw = localStorage.getItem(draftKey);
      const draft = raw ? JSON.parse(raw || "{}") : {};
      const updated = {
        ...draft,
        emailDeliverability,
        verifiedEmail: form.email || "",
      };
      localStorage.setItem(draftKey, JSON.stringify(updated));

      // Also persist into a global cache by email so other screens (like list view) can colorize
      const cacheKey = "emailDeliverabilityCache";
      const cacheRaw = localStorage.getItem(cacheKey);
      const cacheObj = cacheRaw ? JSON.parse(cacheRaw || "{}") : {};
      const key = (form.email || "").toLowerCase();
      if (key) {
        cacheObj[key] = emailDeliverability;
        localStorage.setItem(cacheKey, JSON.stringify(cacheObj));
      }
    } catch {}
  }, [emailDeliverability, form.email, draftKey]);

  // If the email changes compared to the last verified one, reset color to unknown
  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const raw = localStorage.getItem(draftKey);
      const draft = raw ? JSON.parse(raw || "{}") : {};
      const verified = draft?.verifiedEmail || "";
      if (form.email && verified && verified !== form.email) {
        setEmailDeliverability("unknown");
      }
    } catch {}
  }, [form.email, draftKey]);

  const serviceText = useMemo(() => {
    const svc: any = (services as any)?.find(
      (s: any) => (s?.id || s?._id) === form.services
    );
    return (svc && (svc.type || svc.name)) || form.services || "-";
  }, [services, form.services]);

  const isAlreadyScheduled = useMemo(() => {
    try {
      return (
        form.scheduled === true ||
        String((form as any)?.scheduled).toLowerCase() === "true" ||
        (form.leadStatus || "").toLowerCase() === "scheduled"
      );
    } catch {
      return false;
    }
  }, [form.scheduled, form.leadStatus]);

  useEffect(() => {
    if (isAlreadyScheduled && !hasShownScheduledAlert) {
      alert("User has already scheduled");
      setHasShownScheduledAlert(true);
    }
  }, [isAlreadyScheduled, hasShownScheduledAlert]);

  const handleCopyEmail = () => {
    const emailToCopy = (form.email || "").trim();
    if (!emailToCopy) {
      toast.error("No email to copy");
      return;
    }
    const ok = copy(emailToCopy);
    if (ok) {
      toast.success("Email copied to clipboard");
    } else {
      toast.error("Failed to copy email");
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateCampaign();
  };

  const updateCampaign = async (opts?: {
    navigate?: boolean;
    resetToPageOne?: boolean;
  }): Promise<boolean> => {
    if (!editId) return true;
    setIsSubmitting(true);
    try {
      const { scheduled, ...rest } = form as any;
      const payload: any = {
        ...rest,
        campaignId: form.campaignId || campaignId,
        subjectData: form.subjectData,
        EmailData: form.EmailData,
        LinkedInData: form.LinkedInData,
        personalization: form.personalization || form.LinkedInData || "",
        Follow1: form.follow1,
        Follow2: form.follow2,
        Follow3: form.follow3,
        Follow4: form.follow4,
        SubjectData: form.SubjectData,
        SubjectF1: form.SubjectF1,
        SubjectF2: form.SubjectF2,
        SubjectF3: form.SubjectF3,
        SubjectF4: form.SubjectF4,
      };
      if (typeof scheduled === "boolean") {
        payload.scheduled = scheduled;
      }
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.campaignEdit}${editId}`,
        data: payload,
      });
      toast.success("Campaign updated");
      const targetCampaignId =
        (form.campaignId as any) || (campaignId as any) || "";
      if ((opts?.navigate ?? true) && targetCampaignId) {
        const returnPage = opts?.resetToPageOne
          ? "1"
          : new URLSearchParams(window.location.search).get("page") || "1";
        console.log(
          "Schedule updateCampaign - Navigating back with page:",
          returnPage,
          "campaignId:",
          targetCampaignId
        );
        navigate(`/campaign-list/${targetCampaignId}?page=${returnPage}`);
      }
      return true;
    } catch (err: any) {
      toast.error(err?.message || "Failed to update campaign");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSchedule = async () => {
    // Prevent scheduling if already scheduled
    const alreadyScheduled =
      form.scheduled === true ||
      String((form as any)?.scheduled).toLowerCase() === "true" ||
      (form.leadStatus || "").toLowerCase() === "scheduled";
    if (alreadyScheduled) {
      alert("Already scheduled with this email");
      return;
    }
    try {
      setIsScheduling(true);
      setError(null);
      // Validate essentials
      const campaignToUse = instantlyCampaignId?.trim();
      if (!campaignToUse) {
        toast.error(
          "Instantly campaign not detected. Ensure names match or map it."
        );
        return;
      }
      if (!form.email?.trim()) {
        toast.error("Email is required to schedule.");
        return;
      }
      try {
        const endpoint =
          typeof campaignId === "string" && campaignId
            ? `${apiPaths.campaignGetList}?campaignId=${campaignId}&page=1&limit=10000`
            : `${apiPaths.campaignGetAll}?page=1&limit=10000`;
        const listResp = await ApiService({ method: "GET", endpoint });
        const sourceList = Array.isArray(listResp?.campaigns)
          ? listResp.campaigns
          : Array.isArray(listResp?.data)
          ? listResp.data
          : Array.isArray(listResp?.items)
          ? listResp.items
          : Array.isArray(listResp)
          ? listResp
          : [];
        const normalize = (s: any) =>
          typeof s === "string"
            ? s.trim().toLowerCase().replace(/\/+$/, "")
            : "";
        const emailKey = normalize(form.email);
        const websiteKey = normalize(form.companyWebsite);
        const currentId = (editId ||
          (routeId as any) ||
          (campaignId as any)) as any;
        const hasDuplicate = (sourceList || []).some((it: any) => {
          const itId = it?._id || it?.id;
          if (currentId && itId && String(itId) === String(currentId))
            return false;
          const itEmail = normalize(it?.email);
          const itWebsite = normalize(it?.companyWebsite);
          const itScheduled =
            it?.scheduled === true ||
            String(it?.scheduled).toLowerCase() === "true" ||
            String(it?.leadStatus || "").toLowerCase() === "scheduled";
          if (!itScheduled) return false;
          return (
            (emailKey && itEmail && itEmail === emailKey) ||
            (websiteKey && itWebsite && itWebsite === websiteKey)
          );
        });
        if (hasDuplicate) {
          toast.error("Already scheduled with this email or company website");
          return;
        }
      } catch (e: any) {
        toast.error(e?.message || "Duplicate check failed");
        return;
      }

      const nameParts = (form.leadName || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      // Map UI fields to Instantly expected variables
      // Our UI labels show:
      // - "Personalization" bound to LinkedInData
      // - "Follow 1" bound to EmailData
      // - "Follow 2" bound to follow2
      // Ensure payload uses these values even if the canonical fields are empty
      const personalizationText = (
        form.personalization ||
        form.LinkedInData ||
        ""
      ).trim();
      const follow1Text = (form.follow1 || form.EmailData || "").trim();
      const follow2Text = (form.follow2 || "").trim();
      const follow3Text = (form.follow3 || "").trim();

      const payload: any = {
        campaign: campaignToUse,
        email: form.email?.trim(),
        first_name: firstName,
        last_name: lastName,
        EmailData: form.EmailData || "",
        LinkedInData: form.LinkedInData || "",
        subjectData: form.subjectData || "",
        campaignLeadId: editId || (routeId as any) || (campaignId as any),
        company_name: form.companyName || "",
        website: form.companyWebsite || "",
        lt_interest_status: 1,
        skip_if_in_workspace: true,
        skip_if_in_campaign: true,
        skip_if_in_list: true,
        custom_variables: {
          past_customer: false,
          follow_1: follow1Text,
          follow_2: follow2Text,
          follow_3: follow3Text,
          follow_4: (form.follow4 || "").trim(),
          personalization: personalizationText,
          // email_subject_p: personalizationText,
          email_subject_p: form.SubjectData || "",
          email_subject_f1: form.SubjectF1 || "",
          email_subject_f2: form.SubjectF2 || "",
          email_subject_f3: form.SubjectF3 || "",
          email_subject_f4: form.SubjectF4 || "",
        },
      };

      const res = await ApiService({
        method: "POST",
        endpoint: apiPaths.instantlyCampaignsSchedule,
        data: payload,
      });

      toast.success("Lead scheduled to Instantly");
      try {
        const idToUpdate = editId || (routeId as any) || (campaignId as any);
        if (idToUpdate) {
          const { scheduled: _scheduled, ...rest } = form as any;
          const updatePayload: any = {
            ...rest,
            campaignId: form.campaignId || campaignId,
            subjectData: form.subjectData,
            EmailData: form.EmailData,
            Follow1: form.follow1 || form.EmailData,
            Follow2: form.follow2,
            Follow3: form.follow3,
            Follow4: form.follow4,
            SubjectData: form.SubjectData,
            SubjectF1: form.SubjectF1,
            SubjectF2: form.SubjectF2,
            SubjectF3: form.SubjectF3,
            SubjectF4: form.SubjectF4,
            scheduled: true,
            leadStatus: "Scheduled",
          };
          await ApiService({
            method: "PUT",
            endpoint: `${apiPaths.campaignEdit}${idToUpdate}`,
            data: updatePayload,
          });
          setForm((prev: any) => ({
            ...prev,
            scheduled: true,
            leadStatus: "Scheduled",
          }));
        }
        const targetCampaignId =
          (form.campaignId as any) || (campaignId as any) || "";
        if (targetCampaignId) {
          const currentSearch = window.location.search;
          const params = new URLSearchParams(currentSearch);
          const returnPage = params.get("page") || "1";
          console.log(
            "Schedule - Navigating back with page:",
            returnPage,
            "campaignId:",
            targetCampaignId
          );
          navigate(`/campaign-list/${targetCampaignId}?page=${returnPage}`);
        } else {
          console.error(
            "Schedule - No targetCampaignId found, cannot navigate back"
          );
        }
      } catch (e: any) {
        toast.error(e?.message || "Failed to store schedule in database");
        return;
      }
      return res;
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to schedule"
      );
      setError(err?.message || "Failed to schedule");
      const targetCampaignId =
        (form.campaignId as any) || (campaignId as any) || "";
      if (targetCampaignId) {
        const currentSearch = window.location.search;
        const params = new URLSearchParams(currentSearch);
        const returnPage = params.get("page") || "1";
        navigate(`/campaign-list/${targetCampaignId}?page=${returnPage}`);
      }
    } finally {
      setIsScheduling(false);
    }
  };

  const openConfirm = () => {
    const alreadyScheduled =
      form.scheduled === true ||
      String((form as any)?.scheduled).toLowerCase() === "true" ||
      (form.leadStatus || "").toLowerCase() === "scheduled";
    if (alreadyScheduled) {
      toast.error("User has already scheduled");
      return;
    }
    setIsConfirmOpen(true);
  };
  const closeConfirm = () => setIsConfirmOpen(false);

  return (
    <div className="m-5 h-[85vh] overflow-auto">
      <div className="flex justify-between items-center text-center">
        <div className="flex flex-col items-start mx-5">
          <h2 className="flex justify-start  text-xl font-semibold px-1 text-red-500">
            {campaignName ||
              campaignIdToName.get(String(form.campaignId)) ||
              "-"}
          </h2>
          <div className="flex items-center text-xs">
            {emailDeliverability === "undeliverable" ? (
              <p className="flex items-center gap-2 text-sm px-2 text-red-600">
                Email is not deliverable <AlertCircle size={16} />{" "}
              </p>
            ) : emailDeliverability === "deliverable" ? (
              <p className="flex items-center gap-2 text-sm px-2 text-green-600">
                Email is deliverable
                <BadgeCheck size={24} />
              </p>
            ) : (
              ""
            )}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <button
            type="button"
            onClick={() => setIsAiOpen(true)}
            className="px-3 py-1 border rounded"
          >
            AI Data
          </button>
          <button
            type="button"
            onClick={() => setIsQueryOpen(true)}
            className="px-3 py-1 border rounded"
          >
            Query
          </button>
        </div>
      </div>
      <div className="flex justify-end mb-3"></div>
      <form onSubmit={handleUpdate} className="space-y-3 pb-16">
        <div className="bg-white gap-3 p-2 px-4 rounded-lg">
          <div className="flex gap-4 mb-3">
            <div className="break-all text-wrap">
              <label className="block text-xs mb-1">Lead Name</label>
              <p>{form.leadName || "-"}</p>
            </div>
            <div className="break-all text-wrap">
              <label className="block text-xs mb-1">Company Name</label>
              <p>{form.companyName || "-"}</p>
            </div>
            {form.linkedInLeadStatus && (
              <div className="break-all text-wrap">
                <label className="block text-xs mb-1">LinkedIn Status</label>
                <p>{form.linkedInLeadStatus || "-"}</p>
              </div>
            )}
            {form.linkedInLink && (
              <div className="break-all text-wrap">
                <label className="block text-xs mb-1 break-all text-wrap">
                  LinkedIn URL
                </label>
                <a
                  href={form.linkedInLink || "-"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:border-b border-blue-600"
                >
                  {form.linkedInLink || "-"}
                </a>
              </div>
            )}
            {form.companyWebsite && (
              <div className="break-all text-wrap">
                <label className="block text-xs mb-1">Company Website</label>
                <a
                  href={form.companyWebsite || "-"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:border-b border-blue-600"
                >
                  {form.companyWebsite || "-"}
                </a>
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 w-full m-auto gap-2">
            <div className="flex flex-col gap-2">
              <div className="w-full">
                <label className="block text-xs mb-1">Subject_P</label>
                <input
                  name="SubjectData"
                  value={form.SubjectData}
                  onChange={handleChange}
                  className="w-full border rounded px-2 py-1 mb-2"
                />
              </div>
              <div className="w-full">
                <label className="block text-xs mb-1">Personalization</label>
                <textarea
                  name="LinkedInData"
                  value={form.LinkedInData}
                  onChange={handleChange}
                  rows={20}
                  className="w-full border rounded px-2 py-1"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="w-full">
                <label className="block text-xs mb-1">Subject_F1</label>
                <input
                  name="SubjectF1"
                  value={form.SubjectF1}
                  onChange={handleChange}
                  className="w-full border rounded px-2 py-1 mb-2"
                />
              </div>
              <div className="w-full">
                <label className="block text-xs mb-1">Follow 1</label>
                <textarea
                  name="EmailData"
                  value={form.EmailData}
                  onChange={handleChange}
                  rows={20}
                  className="w-full border rounded px-2 py-1"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="w-full">
                <label className="block text-xs mb-1">Subject_F2</label>
                <input
                  name="SubjectF2"
                  value={form.SubjectF2}
                  onChange={handleChange}
                  className="w-full border rounded px-2 py-1 mb-2"
                />
              </div>
              <div className="w-full">
                <label className="block text-xs mb-1">Follow 2</label>
                <textarea
                  name="follow2"
                  value={form.follow2}
                  onChange={handleChange}
                  rows={20}
                  className="w-full border rounded px-2 py-1"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="w-full">
                <label className="block text-xs mb-1">Subject_F3</label>
                <input
                  name="SubjectF3"
                  value={form.SubjectF3}
                  onChange={handleChange}
                  className="w-full border rounded px-2 py-1 mb-2"
                />
              </div>
              <div className="w-full">
                <label className="block text-xs mb-1">Follow 3</label>
                <textarea
                  name="follow3"
                  value={form.follow3}
                  onChange={handleChange}
                  rows={20}
                  className="w-full border rounded px-2 py-1"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="w-full">
                <label className="block text-xs mb-1">Subject_F4</label>
                <input
                  name="SubjectF4"
                  value={form.SubjectF4}
                  onChange={handleChange}
                  className="w-full border rounded px-2 py-1 mb-2"
                />
              </div>
              <div className="w-full">
                <label className="block text-xs mb-1">Follow 4</label>
                <textarea
                  name="follow4"
                  value={form.follow4}
                  onChange={handleChange}
                  rows={20}
                  className="w-full border rounded px-2 py-1"
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-3 w-full space-y-3 rounded-lg p-2 px-4 ">
            {form.email && (
              <div className="break-all text-wrap">
                <label className="block text-xs mb-1">Email</label>
                <button
                  type="button"
                  onClick={handleCopyEmail}
                  className={`px-2 py-1 border rounded text-sm`}
                  title="Copy email"
                >
                  Copy Email
                </button>
              </div>
            )}
            {form.services && (
              <div className="break-all text-wrap">
                <label className="block text-xs mb-1">Service</label>
                <p>{serviceText || "-"}</p>
              </div>
            )}
            {form.leadStatus && (
              <div className="break-all text-wrap">
                <label className="block text-xs mb-1">Lead Status</label>
                <p>{form.leadStatus || "-"}</p>
              </div>
            )}

            {/*
             */}
            {form.leadWebLink && (
              <div className="break-all text-wrap">
                <label className="block text-xs mb-1">Lead Web Link</label>
                <a
                  href={form.leadWebLink || "-"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:border-b border-blue-600"
                >
                  {form.leadWebLink || "-"}
                </a>
              </div>
            )}
            {form.instagramLink && (
              <div className="break-all text-wrap">
                <label className="block text-xs mb-1">Instagram</label>
                <a
                  href={form.instagramLink || "-"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:border-b border-blue-600"
                >
                  {form.instagramLink || "-"}
                </a>
              </div>
            )}

            {/* <div className="break-all text-wrap">
              <label className="block text-xs mb-1">AI Data</label>
              <p
                className="cursor-pointer text-blue-600 hover:underline"
                onClick={() => setIsAiOpen(true)}
                title="Click to view AI Data"
              >
                {form.aiData || "-"}
              </p>
            </div> */}
            {form.subjectData && (
              <div className="break-all text-wrap">
                <label className="block text-xs mb-1">Subject Data</label>
                <p>{form.subjectData || "-"}</p>
              </div>
            )}

            {/* <div className="col-span-2">
              <label className="block text-xs mb-1">Description</label>
              <p>{form.description || "-"}</p>
            </div> */}
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t z-10 flex justify-end gap-2 p-2">
          <button
            type="button"
            onClick={() => {
              // Get the page from URL to maintain it when going back
              const currentSearch = window.location.search;
              const params = new URLSearchParams(currentSearch);
              const returnPage = params.get("page") || "1";
              const targetCampaignId =
                (form.campaignId as any) || (campaignId as any) || "";
              if (targetCampaignId) {
                navigate(
                  `/campaign-list/${targetCampaignId}?page=${returnPage}`
                );
              } else {
                navigate(-1);
              }
            }}
            className="px-3 py-1 border rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
          >
            Save
          </button>
          <button
            type="button"
            disabled={
              isScheduling ||
              emailDeliverability === "undeliverable" ||
              isAlreadyScheduled
            }
            className="px-3 py-1 bg-emerald-600 text-white rounded disabled:opacity-50"
            onClick={openConfirm}
          >
            {isScheduling ? "Scheduling..." : "Schedule"}
          </button>
        </div>
      </form>
      <Modal
        isOpen={isConfirmOpen}
        onClose={closeConfirm}
        style="max-width: 32rem; width: 100%"
      >
        <h3 className="text-lg font-semibold mb-3">Confirm Schedule</h3>
        <div className="flex flex-col justify-start items-start space-y-2 text-sm text-red-500 font-semibold text-start">
          <p className="text-center">
            Campaign Name ={" "}
            {campaignName ||
              campaignIdToName.get(String(form.campaignId)) ||
              "-"}{" "}
          </p>
          <p className="text-center">
            {form.leadName || form.leadName || "-"}{" "}
          </p>
        </div>
        <div className="flex justify-center items-center  gap-2 pt-4">
          <button
            type="button"
            onClick={closeConfirm}
            className="px-3 py-1 border rounded"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={async () => {
              await handleSchedule();
              closeConfirm();
            }}
            disabled={
              isScheduling ||
              emailDeliverability === "undeliverable" ||
              isAlreadyScheduled
            }
            className="px-3 py-1 bg-emerald-600 text-white rounded disabled:opacity-50"
          >
            {isScheduling ? "Scheduling..." : "Confirm"}
          </button>
        </div>
      </Modal>
      <Modal
        isOpen={isQueryOpen}
        onClose={() => setIsQueryOpen(false)}
        style="max-width: 48rem; width: 100%; max-height: 30vh;"
      >
        <h3 className="text-lg font-semibold mb-3">Query</h3>
        <div className="text-sm break-words whitespace-pre-wrap bg-gray-200 p-2 w-[20rem] rounded-lg overflow-auto flex flex-col gap-2">
          <textarea
            name="query"
            value={form.query}
            onChange={handleChange}
            className="w-full border rounded px-2 py-1"
          />

          <button
            type="button"
            onClick={async () => {
              await updateCampaign({ navigate: false });
              setIsQueryOpen(false);
            }}
            className="px-3 py-1 bg-blue-600 text-white rounded"
          >
            Done
          </button>
        </div>
      </Modal>
      <Modal
        isOpen={isAiOpen}
        onClose={() => setIsAiOpen(false)}
        style="max-width: 48rem; width: 100%; max-height: 30vh;"
      >
        <h3 className="text-lg font-semibold mb-3">Full Response</h3>
        {(() => {
          const isUrl = (s: any) =>
            typeof s === "string" && /^https?:\/\//i.test(s);
          const formatValue = (val: any): any => {
            if (val === null || val === undefined || val === "") return "-";
            if (Array.isArray(val)) return val.length ? val : "-";
            if (typeof val === "object") return JSON.stringify(val, null, 2);
            return String(val);
          };
          return (
            <div className="space-y-4 text-sm h-[70vh] overflow-auto">
              <div className="grid grid-cols-2 gap-3">
                {/* <div>
                  <p className="text-xs text-gray-500">date</p>
                  <p className="break-words">{formatValue(extra?.date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">companyName</p>
                  <p className="break-words">{formatValue(form.companyName)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">email</p>
                  <p className="break-words">{formatValue(form.email)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">leadName</p>
                  <p className="break-words">{formatValue(form.leadName)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">linkedInLeadStatus</p>
                  <p className="break-words">
                    {formatValue(form.linkedInLeadStatus)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">leadStatus</p>
                  <p className="break-words">{formatValue(form.leadStatus)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">services (serviceId)</p>
                  <p className="break-words">{formatValue(form.services)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">instagramLink</p>
                  {isUrl(form.instagramLink) ? (
                    <a
                      href={form.instagramLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:border-b border-blue-600 break-all"
                    >
                      {form.instagramLink}
                    </a>
                  ) : (
                    <p className="break-words">
                      {formatValue(form.instagramLink)}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-gray-500">companyWebsite</p>
                  {isUrl(form.companyWebsite) ? (
                    <a
                      href={form.companyWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:border-b border-blue-600 break-all"
                    >
                      {form.companyWebsite}
                    </a>
                  ) : (
                    <p className="break-words">
                      {formatValue(form.companyWebsite)}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-gray-500">leadWebLink</p>
                  {isUrl(form.leadWebLink) ? (
                    <a
                      href={form.leadWebLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:border-b border-blue-600 break-all"
                    >
                      {form.leadWebLink}
                    </a>
                  ) : (
                    <p className="break-words">
                      {formatValue(form.leadWebLink)}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-gray-500">linkedInLink</p>
                  {isUrl(form.linkedInLink) ? (
                    <a
                      href={form.linkedInLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:border-b border-blue-600 break-all"
                    >
                      {form.linkedInLink}
                    </a>
                  ) : (
                    <p className="break-words">
                      {formatValue(form.linkedInLink)}
                    </p>
                  )}
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">description</p>
                  <p className="break-words">{formatValue(form.description)}</p>
                </div> */}
                <div>
                  <p className="text-xs text-gray-500">app</p>
                  <p className="break-words">{formatValue(extra?.app)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">app_probability</p>
                  <p className="break-words">
                    {formatValue(extra?.app_probability)}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">indicators</p>
                  {typeof extra?.indicators === "object" &&
                  !Array.isArray(extra?.indicators) ? (
                    <pre className="whitespace-pre-wrap break-words bg-gray-50 p-2 rounded">
                      {formatValue(extra?.indicators)}
                    </pre>
                  ) : (
                    <p className="break-words">
                      {formatValue(extra?.indicators)}
                    </p>
                  )}
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">dev_opportunities</p>
                  {typeof extra?.dev_opportunities === "object" &&
                  !Array.isArray(extra?.dev_opportunities) ? (
                    <pre className="whitespace-pre-wrap break-words bg-gray-50 p-2 rounded">
                      {formatValue(extra?.dev_opportunities)}
                    </pre>
                  ) : (
                    <p className="break-words">
                      {formatValue(extra?.dev_opportunities)}
                    </p>
                  )}
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">ai_potential</p>
                  {typeof extra?.ai_potential === "object" &&
                  !Array.isArray(extra?.ai_potential) ? (
                    <pre className="whitespace-pre-wrap break-words bg-gray-50 p-2 rounded">
                      {formatValue(extra?.ai_potential)}
                    </pre>
                  ) : (
                    <p className="break-words">
                      {formatValue(extra?.ai_potential)}
                    </p>
                  )}
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">bde_recommendation</p>
                  <p className="break-words">
                    {formatValue(extra?.bde_recommendation)}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">source_urls</p>
                  {Array.isArray(extra?.source_urls) ? (
                    <ul className="list-disc pl-5 space-y-1">
                      {extra.source_urls.map((u: any, idx: number) => (
                        <li key={idx} className="break-words">
                          {isUrl(u) ? (
                            <a
                              href={u}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:border-b border-blue-600"
                            >
                              {u}
                            </a>
                          ) : (
                            <span>{formatValue(u)}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="break-words">
                      {formatValue(extra?.source_urls)}
                    </p>
                  )}
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">response_file</p>
                  {isUrl(extra?.response_file) ? (
                    <a
                      href={extra.response_file}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:border-b border-blue-600 break-all"
                    >
                      {extra.response_file}
                    </a>
                  ) : (
                    <p className="break-words">
                      {formatValue(extra?.response_file)}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">full_response</p>
                <pre className="whitespace-pre-wrap break-words bg-gray-50 p-2 rounded">
                  {formatValue(extra?.full_response)}
                </pre>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">SubjectData</p>
                <pre className="whitespace-pre-wrap break-words bg-gray-50 p-2 rounded">
                  {formatValue(form.SubjectData || form.subjectData)}
                </pre>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">EmailData</p>
                <pre className="whitespace-pre-wrap break-words bg-gray-50 p-2 rounded">
                  {formatValue(form.EmailData)}
                </pre>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">LinkedInData</p>
                <pre className="whitespace-pre-wrap break-words bg-gray-50 p-2 rounded">
                  {formatValue(form.LinkedInData)}
                </pre>
              </div>
            </div>
          );
        })()}
        <div className="flex justify-end gap-2 pt-4">
          {/* <button
            type="button"
            onClick={() => setIsAiOpen(false)}
            className="px-3 py-1 border rounded"
          >
            Close
          </button> */}
        </div>
      </Modal>
    </div>
  );
};

export default Schedule;
